import styles from './style.module.scss'
import Button from '../../../../../components/Button'
import MotionWrapper from '../../../../../layouts/MotionWrapper'
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Step } from '../../../../../hooks/useEditJob';
import { CodeBlock, dracula } from 'react-code-blocks';
import { ChevronDownIcon, ChevronUpIcon, WarningIcon } from '../../../../../assets/icons';
import { CodingExamDetailForEdit } from '../../../../../store/workspace/types';
import { editExamThunk } from '../../../../../store/workspace/thunks';
import { useDispatch } from '../../../../../store/store';
import Loading from '../../../../../components/Loading';

interface Props {
    jobId: string | null;
    setStep: Dispatch<SetStateAction<Step>>;
    goToPreviousStep: () => void;
}

export default function EditCodingExamDetail(props: Props) {
    const { setStep, jobId, goToPreviousStep } = props;
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [openedaAcordions, setOpenedaAcordions] = useState<number[]>([]);
    const [selectedLang, setSelectedLang] = useState('');
    const [examData, setExamData] = useState<CodingExamDetailForEdit | null>(null);
    const questions = examData?.questions[selectedLang] || [];

    useEffect(() => {
        if (!jobId) return;
        setIsLoading(true);
        dispatch(editExamThunk({
            action: 'coding',
            jobId,
            exam: null
        }))
            .unwrap()
            .then((res) => {
                setExamData(res?.examInfo || null);
                setSelectedLang(res?.examInfo?.languages[0] || '');
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [jobId]);

    const manageAcordions = (index: number) => {
        const check = openedaAcordions.findIndex(i => i === index);
        if (check === -1) {
            setOpenedaAcordions(prev => [...prev, index]);
        } else {
            setOpenedaAcordions(prev => prev.filter(i => i !== index));
        }
    };

    return (
        <MotionWrapper className={styles.container}>
            <span className={styles.title}>Step 4 - Coding exam</span>

            <div className={styles.info}>
                <WarningIcon />
                <span>Editing coding exam will be available soon.</span>
            </div>

            {isLoading && <Loading />}

            {examData && !isLoading && <div className={styles.main}>
                <div className={styles.header}>
                    <span>Programming Languages :</span>
                    {examData.languages.map(i => <span
                        onClick={() => setSelectedLang(i)}
                        className={styles.lang}
                        data-is-selected={selectedLang === i}
                    >
                        {i}
                    </span>)}
                </div>
                <div className={styles.question_data}>
                    <span>Question 1:</span>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(0)}
                        >
                            <span>Description</span>
                            {openedaAcordions.some(i => i === 0) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 0) &&
                            <pre className={styles.accordion_body}>
                                {questions[0]?.description}
                            </pre>}
                    </div>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(6)}
                        >
                            <span>Test Cases:</span>
                            {openedaAcordions.some(i => i === 6) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 6) &&
                            <div className={styles.accordion_body}>
                                <CodeBlock
                                    text={questions[0]?.test_cases}
                                    language={examData.languages[0]}
                                    showLineNumbers={true}
                                    theme={dracula}
                                />
                            </div>}
                    </div>
                </div>
                <div className={styles.question_data}>
                    <span>Question 2:</span>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(2)}
                        >
                            <span>Description</span>
                            {openedaAcordions.some(i => i === 2) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 2) &&
                            <pre className={styles.accordion_body}>
                                {questions[1]?.description}
                            </pre>}
                    </div>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(7)}
                        >
                            <span>Test Cases:</span>
                            {openedaAcordions.some(i => i === 7) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 7) &&
                            <div className={styles.accordion_body}>
                                <CodeBlock
                                    text={questions[1]?.test_cases}
                                    language={examData.languages[0]}
                                    showLineNumbers={true}
                                    theme={dracula}
                                />
                            </div>}
                    </div>
                </div>
                <div className={styles.question_data}>
                    <span>Question 3:</span>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(4)}
                        >
                            <span>Description</span>
                            {openedaAcordions.some(i => i === 4) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 4) &&
                            <pre className={styles.accordion_body}>
                                {questions[2]?.description}
                            </pre>}
                    </div>
                    <div className={styles.accordion}>
                        <div
                            className={styles.accordion_header}
                            onClick={() => manageAcordions(8)}
                        >
                            <span>Test Cases:</span>
                            {openedaAcordions.some(i => i === 8) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {openedaAcordions.some(i => i === 8) &&
                            <div className={styles.accordion_body}>
                                <CodeBlock
                                    text={questions[2]?.test_cases}
                                    language={examData.languages[0]}
                                    showLineNumbers={true}
                                    theme={dracula}
                                />
                            </div>}
                    </div>
                </div>
            </div>}

            <div className={styles.actions}>
                <Button
                    text='Back'
                    type='Outline-Primary'
                    className={styles.actions_btn}
                    onClick={goToPreviousStep}
                />
                <Button
                    type='Primary'
                    text='Continue'
                    className={styles.actions_btn}
                    onClick={() => setStep('ExamDeadline')}
                />
            </div>
        </MotionWrapper>
    )
}
